import React, { useCallback, useEffect, useState } from 'react'
import type { SoundDesign } from '@/features/sound-design/domain/models/sound-design.ts'
import Icon from '../../common/Icon'
import Dragger from '../../common/Dragger.tsx'
import Button from '../../common/Button.tsx'
import useAudioStore from '../../../shared/store/audio.store.tsx'
import { formatSecondsToMinutesSeconds } from '@/utils/formatters.ts'
import { useShallow } from 'zustand/react/shallow'
import { addDraggingSoundDesignClip } from '@/features/clips/domain/use-cases/add-dragging-sound-design-clip.ts'
import InteractiveAudioWave from '../../common/InteractiveAudioWave.tsx'
import { useAppStore } from '@/shared/store/app.store.ts'

interface SoundDesignBoxProps {
  sound: SoundDesign
}

const SoundDesignBox: React.FC<SoundDesignBoxProps> = ({ sound }) => {
  const {
    playAudioUrl,
    pausePlayback,
    isPlaying,
    stopPlayback,
    progress,
    currentAudioId,
    setProgress,
    isPaused,
    isFetching,
  } = useAudioStore(
    useShallow((state) => ({
      playAudioUrl: state.playAudioUrl,
      pausePlayback: state.pausePlayback,
      isPlaying: state.isPlaying,
      stopPlayback: state.stopPlayback,
      progress: state.progress,
      currentAudioId: state.currentAudioId,
      setProgress: state.setProgress,
      isPaused: state.isPaused,
      isFetching: state.isFetching,
    }))
  )
  const [isCurrentAudioInContext, setIsCurrentAudioInContext] = useState(false)
  const [isCurrentAudioPlaying, setIsCurrentAudioPlaying] = useState(false)
  const [playbackProgress, setPlaybackProgress] = useState(0)

  const session = useAppStore((state) => state.session)

  useEffect(() => {
    setIsCurrentAudioInContext(currentAudioId === sound.soundSrc)
    setIsCurrentAudioPlaying(isCurrentAudioInContext && isPlaying)
  }, [currentAudioId, isCurrentAudioInContext, sound.soundSrc, isPlaying])

  useEffect(() => {
    if (isCurrentAudioPlaying) {
      setPlaybackProgress(progress)
    }
  }, [progress, isCurrentAudioPlaying])

  const onPlayPauseClick = useCallback(() => {
    if (isCurrentAudioPlaying && !isPaused) {
      void pausePlayback()
    } else {
      if (isCurrentAudioInContext) {
        void playAudioUrl(sound.soundSrc, sound.soundSrc)
      } else {
        void playAudioUrl(sound.soundSrc, sound.soundSrc, playbackProgress)
      }
    }
  }, [
    isCurrentAudioInContext,
    isCurrentAudioPlaying,
    isPaused,
    playbackProgress,
    sound.soundSrc,
  ])

  const updateProgress = useCallback(
    (newProgress: number) => {
      if (isCurrentAudioInContext) {
        setProgress(newProgress)
      } else {
        void playAudioUrl(sound.soundSrc, sound.soundSrc, newProgress)
      }
    },
    [isCurrentAudioInContext, sound.soundSrc, setProgress]
  )

  const handleDragStart = useCallback(async () => {
    stopPlayback()
    await addDraggingSoundDesignClip(
      sound.id,
      sound.title,
      sound.duration ?? 30,
      session?.sessionId ?? 'session-not-generated'
    )
  }, [sound, session?.sessionId])

  return (
    <Dragger onDragStart={handleDragStart}>
      <div className="flex flex-col gap-3 rounded-r-sm bg-zinc-700 p-4 shadow-[0px_0px_8px_-2px_rgba(0,0,0,0.20)]">
        <div className="flex w-full items-center justify-between gap-2">
          <span className="select-text text-lg font-semibold text-white">
            {sound.title}
          </span>
          <span className="text-sm text-neutral-400">
            {sound.duration
              ? formatSecondsToMinutesSeconds(sound.duration)
              : '-'}
          </span>
        </div>

        <div className="flex w-full items-center justify-between gap-1">
          <InteractiveAudioWave
            width={360}
            height={26}
            audioFileUrl={sound.soundSrc}
            progress={isCurrentAudioInContext ? progress : 0}
            setProgress={updateProgress}
            isLoading={isCurrentAudioPlaying && isFetching}
          />
          <Button squared onClick={onPlayPauseClick}>
            <Icon
              name={isCurrentAudioPlaying && !isPaused ? 'pause' : 'play'}
            />
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {sound.genres.map((genre, index) => (
            <div
              key={index}
              className="flex h-5 items-center rounded-sm bg-amber-200 px-2 text-xs font-semibold text-amber-800"
            >
              {genre}
            </div>
          ))}
        </div>
        <div className="flex flex-wrap gap-2">
          {sound.moods.map((mood, index) => (
            <div
              key={index}
              className="flex h-5 items-center rounded-sm bg-zinc-500 px-2 text-xs font-semibold text-white"
            >
              {mood}
            </div>
          ))}
        </div>
      </div>
    </Dragger>
  )
}

export default SoundDesignBox
